/**
 * Size Chart Popup Functionality
 * Handles the size chart popup modal
 */

class SizeChartPopup {
  constructor() {
    this.init();
  }

  init() {
    this.createModal();
    this.bindEvents();
  }

  createModal() {
    // Create modal HTML structure
    const modalHTML = `
      <div id="size-chart-modal" class="size-chart-modal" style="display: none;">
        <div class="size-chart-modal-overlay"></div>
        <div class="size-chart-modal-content">
          <div class="size-chart-modal-header">
            <h3 class="size-chart-modal-title">Size Chart</h3>
            <button class="size-chart-modal-close" type="button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>
          <div class="size-chart-modal-body">
            <div class="size-chart-loading">加载中...</div>
            <div class="size-chart-content" style="display: none;"></div>
          </div>
        </div>
      </div>
    `;

    // Add modal to body
    document.body.insertAdjacentHTML('beforeend', modalHTML);

    // Add CSS styles
    this.addStyles();
  }

  addStyles() {
    const styles = `
      <style id="size-chart-modal-styles">
        .size-chart-modal {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .size-chart-modal-overlay {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: rgba(0, 0, 0, 0.5);
          cursor: pointer;
        }

        .size-chart-modal-content {
          position: relative;
          background: white;
          border-radius: 8px;
          width: 90%;
          max-width: 800px;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
        }

        .size-chart-modal-header {
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          border-bottom: 1px solid #e5e5e5;
          position: relative;
        }

        .size-chart-modal-title {
          margin: 0;
          font-size: 18px;
          font-weight: 600;
          color: #333;
          text-align: center;
        }

        .size-chart-modal-close {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: #666;
          transition: color 0.2s ease;
        }

        .size-chart-modal-close:hover {
          color: #333;
        }

        .size-chart-modal-body {
          flex: 1;
          overflow-y: auto;
          position: relative;
          padding: 20px;
        }

        .size-chart-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 200px;
          color: #666;
        }

        .size-chart-content {
          width: 100%;
        }

        .size-chart-content img {
          max-width: 100%;
          height: auto;
        }

        @media (max-width: 768px) {
          .size-chart-modal-content {
            width: 95%;
            max-height: 95vh;
          }
          
          .size-chart-modal-header {
            padding: 15px;
          }
          
          .size-chart-iframe {
            height: 400px;
          }
        }
      </style>
    `;

    document.head.insertAdjacentHTML('beforeend', styles);
  }

  bindEvents() {
    // Bind click events for size chart buttons
    document.addEventListener('click', (e) => {
      const sizeChartButton = e.target.closest('[data-open-sizeguide]');
      if (sizeChartButton) {
        e.preventDefault();
        this.openModal(sizeChartButton.href);
      }
    });

    // Bind modal close events
    document.addEventListener('click', (e) => {
      if (e.target.matches('.size-chart-modal-overlay') || 
          e.target.closest('.size-chart-modal-close')) {
        this.closeModal();
      }
    });

    // Close modal on ESC key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isModalOpen()) {
        this.closeModal();
      }
    });
  }

  openModal(url) {
    const modal = document.getElementById('size-chart-modal');
    const contentDiv = modal.querySelector('.size-chart-content');
    const loading = modal.querySelector('.size-chart-loading');

    // Show modal
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Show loading
    loading.style.display = 'flex';
    contentDiv.style.display = 'none';

    // Fetch page content via Ajax
    fetch(url)
      .then(response => response.text())
      .then(html => {
        // Parse the HTML to extract page content
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        
        // Try to find the main page content
        let pageContent = '';
        let pageStyles = '';
        let pageScripts = '';
        
        // Look for common content selectors
        const contentSelectors = [
          '.rte',
          '.page-content',
          '.shopify-section-main-page .rte',
          'main .rte',
          '.page-width .rte'
        ];
        
        for (const selector of contentSelectors) {
          const element = doc.querySelector(selector);
          if (element) {
            pageContent = element.innerHTML;
            break;
          }
        }
        
        // If no specific content found, try to get page title and content
        if (!pageContent) {
          const titleElement = doc.querySelector('h1, .page-title');
          const contentElement = doc.querySelector('.page-content, main');
          
          if (titleElement && contentElement) {
            pageContent = `<h1>${titleElement.textContent}</h1>${contentElement.innerHTML}`;
          } else if (contentElement) {
            pageContent = contentElement.innerHTML;
          }
        }
        
        // Extract and process styles and scripts from the content
        if (pageContent) {
          // Create a temporary div to parse the content
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = pageContent;
          
          // Extract styles
          const styleElements = tempDiv.querySelectorAll('style');
          styleElements.forEach(style => {
            pageStyles += style.textContent;
            style.remove(); // Remove from content
          });
          
          // Extract scripts
          const scriptElements = tempDiv.querySelectorAll('script');
          scriptElements.forEach(script => {
            pageScripts += script.textContent;
            script.remove(); // Remove from content
          });
          
          // Get the cleaned content
          pageContent = tempDiv.innerHTML;
        }
        
        // Display the content
        if (pageContent) {
          // Clear content first
          contentDiv.innerHTML = '';
          
          // Add styles to the modal
          if (pageStyles) {
            const styleElement = document.createElement('style');
            styleElement.textContent = pageStyles;
            contentDiv.appendChild(styleElement);
          }
          
          // Add the HTML content
          const contentWrapper = document.createElement('div');
          contentWrapper.innerHTML = pageContent;
          contentDiv.appendChild(contentWrapper);
          
          // Execute scripts after content is added
          if (pageScripts) {
            try {
              // Create a new script element and execute it
              const scriptElement = document.createElement('script');
              scriptElement.textContent = pageScripts;
              contentDiv.appendChild(scriptElement);
            } catch (e) {
              console.error('Error executing page script:', e);
            }
          }
          
          loading.style.display = 'none';
          contentDiv.style.display = 'block';
        } else {
          loading.innerHTML = '未找到页面内容';
        }
      })
      .catch(error => {
        console.error('Error loading page:', error);
        loading.innerHTML = '加载失败，请重试';
      });
  }

  closeModal() {
    const modal = document.getElementById('size-chart-modal');
    const contentDiv = modal.querySelector('.size-chart-content');
    
    modal.style.display = 'none';
    document.body.style.overflow = '';
    
    // Clear content and remove any added styles/scripts
    contentDiv.innerHTML = '';
  }

  isModalOpen() {
    const modal = document.getElementById('size-chart-modal');
    return modal && modal.style.display !== 'none';
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  new SizeChartPopup();
});

// Also initialize if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new SizeChartPopup();
  });
} else {
  new SizeChartPopup();
}
